import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "./ui";
import { t } from "@/lib/i18n";

/* Командная палитра рабочего пространства: один поиск по разделам, командам, людям и замечаниям.
   Открывается кнопкой в шапке окна или сочетанием ⌘K / Ctrl+K. Стрелки — навигация, Enter — открыть, Esc — закрыть. */

export type PaletteItem = {
  id: string;
  label: string;
  icon?: string;
  hint?: string;
  keywords?: string;
  /** якорь раздела на странице — куда проскроллить после выбора */
  anchor?: string;
  /** раздел внутри окна настроек */
  tab?: string;
};

export type PaletteGroup = { id: string; label: string; items: PaletteItem[] };

/** ё и е считаем одной буквой, регистр не важен */
const norm = (v: string) => v.toLowerCase().replace(/ё/g, "е").trim();

function matchRank(item: PaletteItem, q: string) {
  if (!q) return 0;
  const label = norm(item.label);
  if (label.startsWith(q)) return 1;
  if (label.includes(q)) return 2;
  if (norm(`${item.keywords || ""} ${item.hint || ""}`).includes(q)) return 3;
  return 0;
}

function Highlight({ text, query }: { text: string; query: string }) {
  const q = norm(query);
  const i = q ? norm(text).indexOf(q) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark className="rounded-[3px] bg-primary/20 px-0.5 text-foreground">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-border bg-muted/60 px-1.5 py-0.5 font-sans text-[10px] font-semibold leading-none text-muted-foreground">{children}</kbd>;
}

export default function CommandPalette({
  open,
  groups,
  onClose,
  onSelect,
  remote,
  placeholder = t("Введите команду или выполните поиск."),
}: {
  open: boolean;
  groups: PaletteGroup[];
  onClose: () => void;
  onSelect: (item: PaletteItem) => void;
  /** поиск на сервере (от 2 символов): его группы идут после локальных как есть */
  remote?: (q: string) => Promise<PaletteGroup[]>;
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<PaletteGroup[]>([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // при каждом открытии — чистое поле и первый пункт
  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      const id = window.setTimeout(() => inputRef.current?.focus(), 30);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  useEffect(() => {
    const q = query.trim();
    if (!remote || q.length < 2) {
      setFound([]);
      return;
    }
    let alive = true;
    const t = window.setTimeout(() => {
      remote(q).then(
        (g) => alive && setFound(g),
        () => alive && setFound([]),
      );
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [query, remote]);

  const filtered = useMemo(() => {
    const q = norm(query);
    const local = groups
      .map((g) => {
        const items = g.items
          .map((it) => ({ it, rank: matchRank(it, q) }))
          .filter((x) => x.rank > 0 || !q)
          .sort((a, b) => a.rank - b.rank)
          .map((x) => x.it);
        return { ...g, items };
      })
      .filter((g) => g.items.length > 0);
    return [...local, ...found];
  }, [groups, query, found]);

  const flat = useMemo(() => filtered.flatMap((g) => g.items), [filtered]);

  useEffect(() => {
    setActive((a) => (a >= flat.length ? 0 : a));
  }, [flat.length]);

  // активный пункт всегда в зоне видимости списка
  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  if (!open) return null;

  const pick = (item: PaletteItem) => {
    onSelect(item);
    onClose();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!flat.length) return;
      setActive((a) => (e.key === "ArrowDown" ? (a + 1) % flat.length : (a - 1 + flat.length) % flat.length));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const item = flat[active];
      if (item) pick(item);
    }
  };

  let index = -1;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center px-3 pb-6 pt-[8vh] sm:px-4 sm:pt-[10vh]" role="presentation">
      <div className="absolute inset-0 cursor-pointer bg-black/45 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("Поиск по рабочему пространству")}
        className="relative flex w-full max-w-[34rem] flex-col overflow-hidden rounded-card border border-border bg-card text-card-foreground shadow-lift"
      >
        <div className="flex items-center gap-2.5 border-b border-border px-3.5 py-2.5">
          <i className="ri-search-line flex-none text-base leading-none text-muted-foreground" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="command-list"
            aria-autocomplete="list"
            placeholder={placeholder}
            className="h-7 min-w-0 flex-1 border-0 bg-transparent text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          {query ? (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              aria-label={t("Очистить поиск")}
              className="grid size-5 flex-none cursor-pointer place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <i className="ri-close-line text-sm leading-none" aria-hidden />
            </button>
          ) : (
            <Kbd>esc</Kbd>
          )}
        </div>

        <div ref={listRef} id="command-list" role="listbox" aria-label={t("Результаты поиска")} className="max-h-[min(58vh,30rem)] overflow-y-auto p-1.5">
          {flat.length === 0 ? (
            <div className="flex flex-col items-center gap-1.5 px-4 py-10 text-center">
              <i className="ri-search-eye-line text-2xl leading-none text-muted-foreground" aria-hidden />
              <p className="text-[13px] font-semibold">{t("Ничего не найдено")}</p>
              <p className="max-w-[16rem] text-[11.5px] leading-relaxed text-muted-foreground">{t("Попробуйте другое слово — например, «Входящие», «Настройки» или имя клиента.")}</p>
            </div>
          ) : (
            filtered.map((g) => (
              <div key={g.id} className="pb-1.5 last:pb-0">
                <p className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.label}</p>
                {g.items.map((item) => {
                  index += 1;
                  const i = index;
                  const isActive = i === active;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      data-idx={i}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => pick(item)}
                      className={cn(
                        "flex w-full cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] transition",
                        isActive ? "bg-primary/12 text-foreground" : "text-foreground/85 hover:bg-muted",
                      )}
                    >
                      <i className={cn(item.icon || "ri-search-line", "flex-none text-[15px] leading-none", isActive ? "text-primary" : "text-muted-foreground")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">
                        <Highlight text={item.label} query={query} />
                      </span>
                      {item.hint ? <span className="max-w-[45%] flex-none truncate text-[10.5px] text-muted-foreground">{item.hint}</span> : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border bg-background/60 px-3.5 py-2 text-[10.5px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            {t("навигация")}
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd>↵</Kbd>
            {t("открыть")}
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd>esc</Kbd>
            {t("закрыть")}
          </span>
          <span className="ms-auto hidden items-center gap-1 sm:inline-flex">
            <i className="ri-command-line text-[12px] leading-none" aria-hidden />
            {t("поиск по всему пространству")}
          </span>
        </div>
      </div>
    </div>
  );
}